import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom"; // Use useHistory for row clicks
import {
  Avatar,
  Box,
  Typography,
  LinearProgress,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { AuthContext } from "../../../context/AuthContext";
import ENDPOINT from "../../../data/ENDPOINT";
import SecurityHeaders from "../../../data/SecurityHeaders";
import GetAssignments from "../../../actions/course/GetAssignments";
import SetPage from "../../../data/SetPage";
import GettingStartedBatchprogress from "./GettingStartedBatchprogress";
import axios from "axios";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

const BatchProgress = () => {
  const { course_url, batch_id } = useParams();
  const { user } = useContext(AuthContext);
  const history = useHistory(); // For programmatic navigation

  const [submissions, setSubmissions] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      // ... (Data fetching logic remains exactly the same as the previous version)
      try {
        setLoading(true);
        setError(null);
        SetPage("Batch Progress");

        const [submissionsRes, assignmentsRes] = await Promise.all([
          axios.post(
            ENDPOINT + "batch-assignment-submissions-overview.php",
            { session: user.SessionId, course_url, batch_id },
            SecurityHeaders
          ),
          GetAssignments({
            payload: { user: user.SessionId, course_url, batch_id },
          }),
        ]);

        if (submissionsRes&&submissionsRes.data.status === "success") {
          setSubmissions(submissionsRes&&submissionsRes.data.data);
        } else {
          throw new Error("Failed to fetch student submissions.");
        }

        if (assignmentsRes&&assignmentsRes.status === "success" && assignmentsRes.report) {
          setReport(assignmentsRes.report[0]);
        } else {
          throw new Error("Failed to fetch course report.");
        }
      } catch (err) {
        setError(err.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [course_url, batch_id, user.SessionId]);
  
  const handleRowClick = (userId) => {
    // Example link, adjust the path as needed
    history.push(`/student/${userId}/progress`); 
  };

  const filteredSubmissions = submissions.filter((item) =>
    `${item.first_name} ${item.last_name}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalAssignments = report&&report.total_assignments || 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4} className="container">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <div className="container py-4">
      <Grid container spacing={4}>
        {/* Main Content: Student Progress Table */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ borderRadius: "16px", overflow: "hidden" }}>
            <Box p={3}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Batch Progress
              </Typography>
               <Typography variant="h6"  className="mb-3 ">
                Tracking {submissions.length} students. Total Assignments: {totalAssignments}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                label="Search Student"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Box>

            <TableContainer>
              <Table aria-label="student progress table">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Submissions</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Progress</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSubmissions.length > 0 ? (
                    filteredSubmissions.map((item) => {
                      const progress = totalAssignments > 0
                          ? (item.submission_count / totalAssignments) * 100
                          : 0;

                      return (
                        <TableRow
                          key={item.user_id}
                          hover
                          onClick={() => handleRowClick(item.user_id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar
                                src={item.profile_pic}
                                sx={{ width: 40, height: 40, mr: 2 }}
                              />
                              <Typography variant="body1">
                                {`${item.first_name} ${item.last_name}`}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="text.secondary">
                              {`${item.submission_count} / ${totalAssignments}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                color={progress < 40 ? "error" : progress < 80 ? "warning" : "success"}
                                sx={{ flexGrow: 1, height: '8px', borderRadius: '4px' }}
                              />
                              <Typography variant="body2" sx={{ ml: 2, minWidth: '35px', textAlign: 'right' }}>
                                {`${Math.round(progress)}%`}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                       <TableCell colSpan={3} align="center">
                          <Typography sx={{ p: 3 }}>No students found.</Typography>
                       </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={5}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: '16px' }}>
                <GettingStartedBatchprogress />
                <Typography variant="h6" mt={2} gutterBottom>Quick Tips</Typography>
                 <Box component="ul" pl={2}>
                    <Box component="li" mb={1}>Make sure you submit assignments on or before the deadline to gain more points.</Box>
                    <Box component="li">Also, reach your milestones to get more points.</Box>
                 </Box>
            </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default BatchProgress;